import { PacketQuestion } from '../../types/packet.schema';
import { PacketAnswer } from '../../types/sellersPacket.schema';
import { getFileNameExtensionFromUrl } from '../../utils';
import { Answer } from './useSellersPacketForm';
import { generateUuid } from '@utils/uuid';

export const packetAnswersToFormAnswers = (
  questions: PacketQuestion[],
  packetAnswers: PacketAnswer[]
): Answer[] => {
  const answers: Answer[] = [];
  for (const question of questions) {
    const questionAnswers = packetAnswers.filter(
      answer => answer.questionId === question.id
    );
    if (
      question.htmlInputType === 'checkbox' ||
      (question.htmlInputType === 'select' && question.multiple)
    ) {
      // For checkboxes, there's a field for every bounded choice, value is a boolean[]
      answers.push({
        id: getFormFieldId(question.id, ''),
        questionId: question.id,
        value: question.boundedChoices.map(
          choice => !!questionAnswers.find(answer => answer.value === choice.text)
        )
      });
    } else if (question.htmlInputType === 'file') {
      // For file inputs, one field contains all the answers, value is a FileInput[]
      answers.push({
        id: getFormFieldId(question.id),
        questionId: question.id,
        value: questionAnswers
          .filter(answer => answer.value !== '')
          .map(answer => ({
            url: answer.url,
            signedId: answer.value,
            file: null,
            extension: getFileNameExtensionFromUrl(answer.url),
            local: false,
            checksum: answer.checksum
          }))
      });
    } else if (
      question.htmlInputType === 'radio' ||
      question.htmlInputType === 'select'
    ) {
      // In certain instances, a single choice select could have more than one answer, we only want to
      // keep the first one that is still in the group of bounded choices.
      const answersText = questionAnswers.map(answer => answer.value);
      const validAnswers = question.boundedChoices.filter(choice =>
        answersText.includes(choice.text)
      );

      answers.push({
        id: getFormFieldId(question.id, generateUuid()),
        questionId: question.id,
        value: validAnswers.length === 0 ? '' : validAnswers[0].text
      });
    } else if (questionAnswers.length === 0) {
      // Add a field for every unanswered question.
      answers.push({
        id: getFormFieldId(question.id, generateUuid()),
        questionId: question.id,
        value: ''
      });
    } else {
      // For everything else, one answer = one field
      questionAnswers.forEach(answer => {
        let value: Date | string;
        if (question.htmlInputType === 'date') {
          value = new Date(answer.value);
          if (isNaN(value as unknown as number)) {
            value = null;
          }
        } else {
          value = answer.value;
        }
        answers.push({
          id: getFormFieldId(answer.questionId, generateUuid()),
          questionId: answer.questionId,
          value
        });
      });
    }
  }
  return answers;
};

// Builds the form field id, these values are used when submitting the packet, internalId ensures new 'multiple' fields maintain uniqueness
export const getFormFieldId = (questionId: string, internalId = ''): string =>
  `[${questionId}][${internalId}]`;
